import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, HostListener, ViewChild, OnDestroy, NgZone } from '@angular/core';
import { CellRendererComponent } from '@valg-shared/components/table/directives/cell-component-outlet/cell-component-outlet.directive';
import { TooltipDirective } from '@valg-shared/components/tooltip/tooltip.directive';
import { isEllipsisActive } from '@valg-shared/utils/helpers';
import { camelToSnakeCase } from '@valg-shared/utils/string-utils';
import { Column } from '../../table/column';
import { first } from 'rxjs/operators';
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: 'valg-text-ellipsis-tooltip-renderer',
  templateUrl: './text-ellipsis-tooltip-renderer.component.html',
  host: { 'class': 'text-ellipsis-tooltip-renderer d-block' },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextEllipsisTooltipRendererComponent implements CellRendererComponent, OnDestroy {
  enableTooltipCheck = true;
  value = "";
  column?: Column;
  showTooltip = true;
  checkTooltip = false;

  @ViewChild('tooltipContainer') tooltipContainer!: ElementRef;
  @ViewChild(TooltipDirective) tooltip!: TooltipDirective;

  constructor(
      protected cdr: ChangeDetectorRef,
      protected readonly ngZone: NgZone,
      protected readonly translateService: TranslateService,
  ) {}

  public init(data: any, column: any): void {
    this.column = column;
    let value = "";
    if(column.path.indexOf('.') !== -1){
      value = data;
      let key = column.path.split('.');
      for (let k of key) {
        value = value[k];
      }
    } else{
      value = data[ column.path ];
    }

    if(!value){
      value = column.metadata.defaultValue ? 
          (this.translateService.instant(column.metadata.defaultValue) ?? data[ column.propKey ]) 
          : data[ column.propKey ];
    }
    if(value !== this.value){
      this.value = value;
      this.checkTooltip = true;
      this.cdr.detectChanges();
    }
  }

  tryGetTranslatedValue(column?: Column): string {
    const rawValue = this.value;
    const translatedValue = column?.metadata?.translationPrefixKey ? `${column.metadata?.translationPrefixKey}.${camelToSnakeCase(rawValue)}` : undefined;
    return translatedValue ?? rawValue;
  }

  ngAfterViewInit(): void {
    if(this.enableTooltipCheck){
      if(this.checkTooltip){
        this.showTooltip = true;
        this.ngZone.onStable.pipe(first()).subscribe(() => {
          this.showTooltip = this.shouldIActivateTooltip();
        });

        this.checkTooltip = false;
        this.cdr.detectChanges();
      }
    }
  }

  shouldIActivateTooltip(){
    if(this.tooltipContainer){
      let container = this.tooltipContainer.nativeElement;
      let span = container.getElementsByTagName('span')[ 0 ];
      let showTooltip = isEllipsisActive(container, span);
      if(!showTooltip){
        this.tooltip?.close();
      }
      return showTooltip;
    }
    return false;
  }

  @HostListener('window:resize')
  onResize() {
    if(this.enableTooltipCheck){
      this.checkTooltip = true;
      this.cdr.detectChanges();
    }
  }

  @HostListener('mouseover')
  onMouseOver() {
    this.ngZone.onStable.pipe(first()).subscribe(() => {
      this.showTooltip = this.shouldIActivateTooltip();
    });
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.tooltip?.close();
  }
}
